import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import { toFullName } from '@repo/toolkit/to-full-name'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Button from '@repo/ui/button'
import { CheckMarkIcon, SendIcon, UserCheckIcon } from '@repo/ui/icons'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import Typography from '@repo/ui/typography'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState } from 'react'
import { toast } from 'sonner'

import { checkAAKeyword } from '../../../../api/aa-keywords/check-aa-keyword'
import { confirmEnrollment } from '../../../../api/enrollments/confirm-enrollment'
import { EnrollmentStatus } from '../../../../api/enrollments/get-enrollment-status'
import { payInstallment } from '../../../../api/enrollments/pay-installment'
import { preauthorize, preauthorizeOtoIndividual } from '../../../../api/enrollments/preauthorize'
import { sendEnrollmentPropositionsEmail } from '../../../../api/enrollments/send-quota'
import { Paths } from '../../../../lib/constants/path'
import { useModalManager } from '../../../../lib/hooks/use-modal-manager'
import { cancellationReasonEnumMapping } from '../../../../lib/types/enums/enrollment/cancellation-reason.enum'
import { Enrollment } from '../../../../lib/types/models/enrollment/enrollment'
import { EnrollmentProperties } from '../../../../lib/types/models/enrollment/enrollment.properties'
import { EnrollmentProposition } from '../../../../lib/types/models/enrollment-proposition/enrollment-proposition'
import { User } from '../../../../lib/types/models/users/user'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { showAxiosError } from '../../../../lib/utils/functions/misc.functions'
import CancelEnrollment from '../cancel-enrollment'
import { CheckAaForm } from '../check-aa-form'
import { EnrollmentPropositionToInputData } from '../utils/enrollments-utils'
import EnrollmentAdvisorsSelection from './enrollment-advisors-selection'
import EnrollmentDuplicateButton from './enrollment-duplicate-button'
import EnrollmentParentsRelatedTripsButton from './enrollment-parents-related-trips-button'
import EnrollmentStatusOverview from './enrollment-status-overview'
import EnrollmentTripHistoryButton from './enrollment-trip-history-button'
import { SendEnrollmentProposalForm } from './send-enrollment-proposal-form'

type EnrollmentFormHeaderProps = {
  enrollment: Pick<
    Enrollment,
    | 'id'
    | 'createdAt'
    | 'updatedAt'
    | 'parent1Job'
    | 'parent2Job'
    | 'isAA'
    | 'cancellationReason'
    | 'confirmedAt'
    | 'paymentStatus'
    | 'medicalStatus'
    | 'cseStatus'
    | 'status'
    | 'parent1Email'
    | 'parent1Gender'
    | 'parent1FirstName'
    | 'parent1LastName'
    | 'parentAuthorizedToGoOutAlone'
    | 'howDidYouHearAboutUsOptionId'
    | 'medicalInformations'
    | 'medicalInformationsEn'
    | 'additionalMedicalInformations'
    | 'additionalMedicalInformationsEn'
    | 'canGoOutAlone'
    | 'medicalLevel'
    | 'propositionType'
  > & {
    createdBy?: Pick<User, 'firstName' | 'lastName'>
    // a One to One Individuel enrollment carries no journey proposition
    mainProposition?: Pick<EnrollmentProposition, 'id'> & EnrollmentPropositionToInputData
    attributedUser?: Pick<User, 'id' | 'firstName' | 'lastName'>
  }
  enrollmentStatus: EnrollmentStatus
  mode: 'detailed' | 'simple'
}

export function EnrollmentFormHeader({
  enrollment,
  enrollmentStatus,
  mode
}: EnrollmentFormHeaderProps) {
  const { navigate } = useNavigator()
  const queryClient = useQueryClient()
  const { id, createdBy, attributedUser } = enrollment

  const { modalState, toggleModal, closeModel } = useModalManager<
    'confirm-enrollment' | 'update-aa-tag' | 'send-enrollment-proposal' | 'age-mismatch'
  >(['confirm-enrollment', 'update-aa-tag', 'send-enrollment-proposal', 'age-mismatch'])

  // set once the admin acknowledged the age mismatch, so the backend lets the confirmation through
  const [forceAgeMismatch, setForceAgeMismatch] = useState(false)

  const { mutate: confirmEnrollmentMutation, isPending: isConfirmEnrollmentPending } = useMutation(
    {
      mutationFn: confirmEnrollment,
      onError: showAxiosError,
      onSuccess: () => {
        toast.success('Inscription confirmée')
        closeModel('confirm-enrollment')
        closeModel('update-aa-tag')
        closeModel('age-mismatch')
        queryClient.invalidateQueries({ queryKey: ['enrollments'] })
        navigate(Paths.ENROLLMENT_LIST_PAGE)
      }
    }
  )

  const { mutate: preauthorizeMutation, isPending: isPreauthorizeLoading } = useMutation({
    mutationFn: preauthorize,
    onError: showAxiosError
  })

  const { mutate: preauthorizeOtoIndividualMutation, isPending: isPreauthorizeOtoLoading } =
    useMutation({
      mutationFn: preauthorizeOtoIndividual,
      onError: showAxiosError
    })

  const { mutate: payInstallmentMutation, isPending: isPayInstallment } = useMutation({
    mutationFn: payInstallment,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Inscription confirmée')
      queryClient.invalidateQueries({ queryKey: ['enrollments'] })
    }
  })

  const { mutate: sendEnrollmentQuotaMutation } = useMutation({
    mutationFn: sendEnrollmentPropositionsEmail,
    onError: showAxiosError,
    onSuccess: () => {
      toast.success('Proposition envoyée')
      closeModel('send-enrollment-proposal')
    }
  })

  const { data: isPossibleAA } = useQuery({
    queryKey: [
      'check-aa-keyword',
      `label-${enrollment.parent1Job}`,
      `label-${enrollment.parent2Job}`
    ],
    queryFn: async (): Promise<boolean> => {
      const parent1AAKeywords = enrollment?.parent1Job
        ? await checkAAKeyword({ label: enrollment.parent1Job })
        : false
      const parent2AAKeywords = enrollment?.parent2Job
        ? await checkAAKeyword({ label: enrollment.parent2Job })
        : false

      return parent1AAKeywords || parent2AAKeywords
    },
    enabled: !!enrollment.id && (!!enrollment?.parent1Job || !!enrollment?.parent2Job)
  })

  // the AA question comes right after the age warning, when it applies at all
  const openConfirmationChain = () => {
    if (isPossibleAA && !enrollment.isAA) toggleModal('update-aa-tag')
    else toggleModal('confirm-enrollment')
  }

  return (
    <div>
      {/* top  */}
      {enrollment?.cancellationReason && (
        <div className={'bg-red-light-hover w-full mb-5 text-red p-4 rounded'}>
          <Typography variant={'text'} className={'mb-1'}>
            Motif de l'annulation
          </Typography>
          <Typography variant={'secondary-body'}>
            {cancellationReasonEnumMapping[enrollment.cancellationReason]}
          </Typography>
        </div>
      )}

      <div className={'flex flex-row gap-2 justify-between mb-5 items-start'}>
        <div className={'flex flex-row gap-2 items-start'}>
          <EnrollmentAdvisorsSelection
            attributedUser={attributedUser}
            enrollmentId={enrollment.id}
          />
          {mode === 'detailed' && (
            <div
              className={'flex flex-col gap-1 bg-white-dark w-fit text-center px-2 py-1 rounded'}
            >
              <Typography
                variant={'label-semibold'}
                color={tailwindColors.grey.lighter}
                className={'whitespace-nowrap'}
              >
                Créé par
              </Typography>
              <Typography variant={'label-semibold'} className={'whitespace-nowrap'}>
                {createdBy ? toFullName(createdBy.firstName, createdBy.lastName) : '-'}
              </Typography>
            </div>
          )}
        </div>

        <div className={'flex flex-row gap-2 items-start'}>
          <EnrollmentTripHistoryButton enrollmentId={enrollment.id} />
          <EnrollmentParentsRelatedTripsButton enrollmentId={enrollment.id} />
          <EnrollmentDuplicateButton enrollment={enrollment} />

          {mode === 'detailed' && (
            <>
              <Button
                variant={'secondary'}
                icon={SendIcon}
                label={'Envoyer notre proposition'}
                onClick={() => toggleModal('send-enrollment-proposal')}
              />
              {!EnrollmentProperties.isCancelled(enrollment) && (
                <CancelEnrollment
                  id={id}
                  paymentStatus={enrollment.paymentStatus}
                  isConfirmed={EnrollmentProperties.isConfirmed(enrollment)}
                  onSuccess={() => navigate(Paths.ENROLLMENT_LIST_PAGE)}
                />
              )}
              <Button
                icon={UserCheckIcon}
                label={"Confirmer l'inscription"}
                disabled={!enrollmentStatus.canConfirmEnrollment}
                onClick={() => {
                  if (!enrollmentStatus.isAgeCompliant) {
                    toggleModal('age-mismatch')
                    return
                  }
                  openConfirmationChain()
                }}
              />
              {import.meta.env.NODE_ENV !== 'production' && (
                <>
                  <Button
                    variant="ghost"
                    label={'Préautorisation'}
                    loading={isPreauthorizeLoading || isPreauthorizeOtoLoading}
                    onClick={() => {
                      if (EnrollmentProperties.isOtoIndividual(enrollment)) {
                        preauthorizeOtoIndividualMutation({ enrollmentId: enrollment.id })
                        return
                      }
                      if (!enrollment.mainProposition) {
                        toast.error('Aucune proposition principale à préautoriser')
                        return
                      }
                      preauthorizeMutation({
                        enrollmentId: enrollment.id,
                        propositionId: enrollment.mainProposition.id
                      })
                    }}
                  />
                  <Button
                    variant="ghost"
                    label={'Payer une échéance'}
                    loading={isPayInstallment}
                    onClick={() => payInstallmentMutation(enrollment.id)}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
      {/* bottom */}
      <EnrollmentStatusOverview enrollment={enrollment} mode="detailed" />

      {/* ================= Modals ================= */}

      {/* send enrollment proposal modal */}
      <Modal
        hideActions
        icon={SendIcon}
        isOpen={modalState['send-enrollment-proposal']}
        onCancel={() => toggleModal('send-enrollment-proposal')}
        title={'Envoyer notre proposition'}
      >
        <SendEnrollmentProposalForm
          mode="enrollment"
          enrollmentId={enrollment.id}
          className={MODAL_BODY_CLASSNAME}
          defaultValues={{ emails: [enrollment.parent1Email] }}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('send-enrollment-proposal'),
            onConfirm: ({ emails }) => sendEnrollmentQuotaMutation({ id, emails })
          })}
        />
      </Modal>

      {/* age mismatch warning modal */}
      <Modal
        icon={CheckMarkIcon}
        variant={'confirmation'}
        isOpen={modalState['age-mismatch']}
        onCancel={() => closeModel('age-mismatch')}
        title={
          "Cette demande d'inscription ne respecte pas l'âge requis pour le séjour sélectionné. Voulez-vous la confirmer quand même ?"
        }
        actionButtonsOptions={{
          cancelText: 'Fermer',
          confirmText: 'Confirmer quand même',
          onConfirm: () => {
            setForceAgeMismatch(true)
            // the admin already confirmed here, so skip the second confirmation modal
            if (isPossibleAA && !enrollment.isAA) {
              closeModel('age-mismatch')
              toggleModal('update-aa-tag')
              return
            }
            confirmEnrollmentMutation({ id, forceAgeMismatch: true })
          }
        }}
        isLoading={isConfirmEnrollmentPending}
      />

      {/* confirm enrollment modal */}
      <Modal
        icon={CheckMarkIcon}
        variant={'confirmation'}
        isOpen={modalState['confirm-enrollment']}
        onCancel={() => closeModel('confirm-enrollment')}
        title={`Confirmez-vous la validation du bulletin de "${EnrollmentProperties.getEnrollmentTitle(enrollment)}" ?`}
        actionButtonsOptions={{
          cancelText: 'Fermer',
          confirmText: 'Confirmer',
          onConfirm: () => confirmEnrollmentMutation({ id, forceAgeMismatch })
        }}
        isLoading={isConfirmEnrollmentPending}
      />

      {/* update aa tag modal */}
      <Modal
        icon={CheckMarkIcon}
        variant={'confirmation'}
        isOpen={modalState['update-aa-tag']}
        onCancel={() => closeModel('update-aa-tag')}
        title={'Avant de confirmer, souhaitez-vous taguer ce bulletin en AA ?'}
        hideActions
        hasBorderBottom={false}
      >
        <CheckAaForm
          className={MODAL_BODY_CLASSNAME}
          buttonsConfig={modalButtonConfig({
            onCancel: () => closeModel('update-aa-tag'),
            onConfirm: (values) => confirmEnrollmentMutation({ id, ...values, forceAgeMismatch })
          })}
        />
      </Modal>
    </div>
  )
}
